import { AppBar, Toolbar, Typography, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';

interface MessagesHeaderProps {
  onSearchChange?: (search: string) => void;
}

export function MessagesHeader({ onSearchChange }: MessagesHeaderProps) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Debounce search with 300ms delay
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      onSearchChange?.(searchValue.trim());
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchValue, onSearchChange]);

  return (
    <AppBar
      position="fixed"
      sx={{
        bgcolor: 'primary.main',
        borderRadius: '0 0 24px 24px',
        zIndex: theme => theme.zIndex.appBar,
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <Toolbar sx={{ flexDirection: 'column', alignItems: 'stretch', py: 2 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2, color: 'primary.contrastText' }}>
          {t('navigation.messages')}
        </Typography>
        <TextField
          fullWidth
          placeholder={t('messages.searchPlaceholder')}
          size="small"
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          data-testid="messages-search-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'primary.contrastText' }} />
              </InputAdornment>
            ),
            sx: {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'primary.contrastText',
              borderRadius: 2,
              '& input::placeholder': { color: 'rgba(255, 255, 255, 0.8)' },
              '& input': { color: 'primary.contrastText' },
            },
          }}
        />
      </Toolbar>
    </AppBar>
  );
}
